/**
 * HTTP
 * @file HTTP请求
 * @author 魏宏裕 <weihongyu12@515cn.com>
 */

import axios from 'axios'
import qs from 'qs'
import { LoadingBar, Message, Modal } from 'iview'
import router from '@/router/router'

import { apiUrlHeader } from './api_url_header'
import { getMyId, getNow } from '@/utils/device-tool'
import moment from 'moment'

let host = apiUrlHeader()

/**
 * HTTP
 * @example <caption>基本使用</caption>
 * import HTTP from './scripts/http.js'
 * const http = new HTTP()
 * http.get(url, params, msg)
 * http.post(url, params, msg)
 * @example <caption>在Vue中使用</caption>
 * this.$http.get(url, params, msg)
 * this.$http.post(url, params, msg)
 */
class HTTP {
  constructor () {
    this.host = host
    this.timeout = 60000
  }

  /**
   * 抛出异常
   * @param {object} response - 异常信息
   * @param {string} msg - 异常提示文字
   * @private
   */
  _throwError (response, msg) {
    if (response) {
      if (response.status >= 400 && response.status < 500) {
        switch (response.status) {
          case 401: // 没有登录或者token失效
            this._errorMsg(response, msg)
            router.replace({name: 'login'})
            break
          case 403: // 没有权限
            this._errorMsg(response, msg)
            router.replace({name: '403'})
            break
          case 404:
            this._errorMsg(response, msg)
            router.push({name: '404'})
            break
          default:
            this._errorMsg(response, msg)
            break
        }
      } else if (response.status >= 500) {
        this._errorMsg(response, msg)
        // router.push({name: '500'})
      }
    } else {
      Message.error('请求出现错误')
      // router.push({name: '500'})
    }
  }

  /**
   * 弹出错误信息
   * @param {object} response - 异常信息
   * @param {string} msg - 异常提示文本
   * @private
   */
  _errorMsg (response, msg) {
    if (msg) {
      Message.error({
        content: msg,
        duration: 4,
        closable: true
      })
    } else if (response.data.message) {
      if (response.data.message.includes('医保')) {
        Message.error({
          content: response.data.message,
          duration: 0,
          closable: true
        })
      } else {
        Message.error({
          content: response.data.message,
          duration: 6,
          closable: true
        })
      }
    } else if (response.data.ExceptionMessage) {
      Message.error(response.data.ExceptionMessage)
    } else if (response.data.data === null) {
      // Message.error('请联系系统管理员')
      this._showDetailErrorModal(response)
    } else {
      Message.error(response.toString())
    }
  }

  /**
   * 显示详细错误信息弹窗
   * @param {object} response
   */
  _showDetailErrorModal (response) {
    const fullUrl = response.config ? response.config.url : '未知'
    const requestMethod = response.config ? response.config.method.toUpperCase() : '未知'

    let requestParams = '无'
    if (response.config) {
      if (response.config.params) {
        // GET请求参数
        requestParams = JSON.stringify(response.config.params, null, 2)
      } else if (response.config.data) {
        // POST请求参数
        try {
          const parsed = qs.parse(response.config.data)
          requestParams = JSON.stringify(parsed, null, 2)
        } catch (e) {
          requestParams = response.config.data
        }
      }
    }

    const responseData = response.data ? JSON.stringify(response.data, null, 2) : '无响应数据'

    const content = `
      <div style="max-height: 500px; overflow-y: auto;">
        <div style="margin-bottom: 15px;">
          <h4 style="margin-bottom: 5px; color: #ed4014;">请求信息：</h4>
          <p style="margin: 5px 0;"><strong>请求方式：</strong>${requestMethod}</p>
          <p style="margin: 5px 0; word-break: break-all;"><strong>请求地址：</strong>${fullUrl}</p>
        </div>
        
        <div style="margin-bottom: 15px;">
          <h4 style="margin-bottom: 5px; color: #ed4014;">请求参数：</h4>
          <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto; font-size: 12px;">${requestParams}</pre>
        </div>
        
        <div>
          <h4 style="margin-bottom: 5px; color: #ed4014;">响应数据：</h4>
          <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto; font-size: 12px;">${responseData}</pre>
        </div>
      </div>
    `

    Modal.error({
      title: '系统错误 - 请联系系统管理员',
      content: content,
      width: 700,
      okText: '关闭'
    })
  }

  getAuthorization () {
    const accessToken = window.sessionStorage.getItem('access_token')
    return accessToken ? `bearer ${accessToken}` : ''
  }

  /**
   * GET
   * @param {string} url - 请求的URL
   * @param {object} params - 请求参数
   * @param {string} [msg] - 抛出异常的提示信息
   * @param headers
   * @return {Promise}
   */
  get (url, params, msg = '', headers = {}) {
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.get(url, {
        params: Object.assign({}, params, { '_t': getNow(), _uid: getMyId() }),
        baseURL: this.host,
        timeout: this.timeout,
        headers: {
          'Authorization': this.getAuthorization(),
          'Content-Type': 'application/x-www-form-urlencoded',
          ...headers
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          msg !== 'none' && this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }
  /**
   * POST
   * @param {string} url - 请求的URL
   * @param {object} params - 请求参数
   * @param {string} [msg] - 抛出异常的提示信息
   * @param headers
   * @return {Promise}
   */
  post (url, params, msg = '', headers = {}) {
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      const et = Object.assign({}, params.__extend, { '_t': getNow(), _uid: getMyId() })
      delete params.__extend
      axios.post(url, qs.stringify(params), {
        params: et,
        baseURL: this.host,
        timeout: this.timeout,
        headers: {
          'Authorization': this.getAuthorization(),
          ...headers
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          msg !== 'none' && this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }

  getJY (url, params, msg = '') {
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.get(url, {
        params,
        baseURL: this.host,
        timeout: this.timeout * 3,
        headers: {
          'Authorization': this.getAuthorization(),
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }

  postJY (url, params, msg = '') {
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.post(url, qs.stringify(params), {
        baseURL: this.host,
        timeout: this.timeout * 3,
        headers: {
          'Authorization': this.getAuthorization()
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        this._throwError(error, msg)
        reject(error)
      })
    })
  }

  postXML (url, params, timeout, msg = '') {
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      axios.post(url, params, {
        baseURL: this.host,
        timeout: timeout || this.timeout,
        headers: {
          'Authorization': this.getAuthorization(),
          'Content-Type': 'application/xml'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        reject(error)
      })
    })
  }

  postJson (url, params, timeout, msg = '') {
    LoadingBar.start()
    return new Promise((resolve, reject) => {
      const et = Object.assign({}, params.__extend, { '_t': getNow(), _uid: getMyId() })
      delete params.__extend
      axios.post(url, params, {
        params: et,
        baseURL: this.host,
        timeout: timeout || this.timeout,
        headers: {
          'Authorization': this.getAuthorization(),
          'Content-Type': 'application/json'
        },
        validateStatus: function (status) {
          return status >= 200 && status < 600
        }
      }).then((response) => {
        if (response.status < 400) {
          LoadingBar.finish()
          resolve(response.data)
        } else {
          LoadingBar.error()
          this._throwError(response, msg)
          reject(response)
        }
      }).catch((error) => {
        LoadingBar.error()
        reject(error)
      })
    })
  }

  /**
   * 读取身份证
   * @description 可以先用短超时（0.5秒）判断是否有身份证读卡器
   * @param {number} timeout 超时时间
   */
  getIDCardInfo (timeout = 4000) {
    return new Promise((resolve, reject) => {
      axios.get('http://localhost:8118/api/idCard/read-sfz-info', {
        timeout: timeout || this.timeout,
        headers: {
          'Content-Type': 'application/json'
        }
      }).then((res) => {
        console.log(res)
        const { data, status } = res
        if (status === 200) {
          if (data.retVal === 0) {
            const gc = {
              '男': '2',
              '女': '3'
            }
            data.patientInfo = {
              name: data.xm,
              gender_code: gc[data.xb],
              ethnicity: `${data.mz}族`,
              id_card_code: data.sfzhm,
              both_date: moment(data.csrq, 'YYYYMMDD').format('YYYY-MM-DD'),
              permanent_address_house_number: data.hjdz
            }
          }
          resolve(data)
        }
      }).catch((e) => {
        const {message} = e
        if (message === 'Network Error') {
          resolve(false)
        } else if (message === 'timeout of 4000ms exceeded') {
          resolve(true)
        } else {
          reject(e)
        }
      })
    })
  }

  /**
   * 请求DB-API的资源
   * @param {string} resId 资源ID
   * @param {Object} data
   * @param {Object} query
   */
  reqDBResource (resId, data = {}, query) {
    const prefix = '/system-api/webapi/third-party/get-result'
    const queryStr = qs.stringify(query)
    const newResId = queryStr ? `${resId}?${queryStr}` : resId
    const params = qs.stringify({
      code: newResId,
      json_param: JSON.stringify(data)
    })
    return new Promise((resolve, reject) => {
      axios.post(prefix, params, {
        baseURL: this.host,
        headers: {
          'Authorization': this.getAuthorization(),
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }).then(res => {
        const { status, data } = res
        if (status === 200) {
          try {
            const _data = JSON.parse(data)
            if (_data.code === 500 && _data.msg !== 'success') reject(_data)
            else resolve(_data)
          } catch (error) {
            resolve(data)
          }
        } else {
          reject(data)
        }
      }).catch(e => {
        reject(e)
      })
    })
  }

  reqConfiguration (itemCode) {
    return new Promise((resolve, reject) => {
      this.get('/system-api/webapi/configuration/getContent', {
        item_code: itemCode
      }).then(res => {
        try {
          const tmp = JSON.parse(res)
          resolve(tmp)
        } catch (error) {
          resolve(res)
        }
      }).catch(e => {
        reject(e)
      })
    })
  }
}

export default HTTP
